import React, { useEffect, useRef } from 'react';
import styles from './ImageUploader.module.css';
import { Camera, CameraResultType } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { validateFile } from '../utils/validationUtils';
import { useError } from '../contexts/ErrorContext';

const ImageUploader = ({ 
  onImagesSelected, 
  multiple = true, 
  currentImages = [], 
  onRemoveImage, 
  style = {},
  className = '',
  size = 'medium' // 'small', 'medium', 'large'
}) => {
  const { showErrorToast } = useError();
  // Track object URLs for cleanup
  const objectUrlsRef = useRef(new Set());
  const sizeClass = size === 'small' ? styles.small : (size === 'large' ? styles.large : '');
  
  // Cleanup function for object URLs
  const cleanupObjectUrls = () => {
    objectUrlsRef.current.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch (error) {
        // Error revoking object URL - handled silently
      }
    });
    objectUrlsRef.current.clear();
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupObjectUrls();
    };
  }, []);

  // Cleanup when currentImages change
  useEffect(() => {
    // Clean up old object URLs when images change
    cleanupObjectUrls();
  }, [currentImages]);

  const validateFileLocal = (file) => {
    const validation = validateFile(file, {
      allowedTypes: ['image/*']
    });
    
    if (!validation.isValid) {
      showErrorToast(validation.error, {
        variant: 'warning',
        autoHide: true,
        duration: 4000
      });
      return false;
    }
    
    return true;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    const validFiles = files.filter(validateFileLocal);
    
    if (validFiles.length > 0) {
      onImagesSelected(validFiles);
    }
  };

  const presentPicker = async () => {
    const platform = Capacitor.getPlatform();
    
    if (platform === 'android') {
      try {
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.DataUrl,
          // The 'source' is omitted to use the prompt configuration from capacitor.config.json
        });
  
        if (image.dataUrl) {
          const response = await fetch(image.dataUrl);
          const blob = await response.blob();
          
          const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
          
          if (validateFileLocal(file)) {
            onImagesSelected([file]);
          }
        }
      } catch (error) {
        if (error.message !== 'User cancelled photos app' && error.message !== 'No image selected') {
          showErrorToast('Error getting image. Please try again', {
            variant: 'error',
            autoHide: true,
            duration: 4000
          });
        }
      }
    } else {
      // iOS and Web: use file input (more reliable)
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = multiple;
      input.style.display = 'none';
      
      // Cleanup function for file input
      const cleanupInput = () => {
        try {
          if (input.parentNode) {
            input.parentNode.removeChild(input);
          }
        } catch (error) {
          // Error cleaning up file input - handled silently
        }
      };

      input.onchange = (e) => {
        handleFileSelect(e);
        cleanupInput();
      };

      input.oncancel = () => {
        cleanupInput();
      };
      
      document.body.appendChild(input);
      input.click();
    }
  };



  return (
    <div className={`image-upload-container ${styles.container} ${className}`} style={style}>
      <div 
        className={`image-upload-label ${styles.surface} ${sizeClass}`}
        onClick={() => {
          presentPicker();
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            presentPicker();
          }
        }}
      >
        <div className={`upload-placeholder ${styles.placeholder}`}>
          <div className={styles.iconWrapper}>
            <img src="/images/upload-icon.png" alt="Upload" className={styles.icon} />
          </div>
          <span className={styles.text}>Click to upload images</span>
        </div>
      </div>
    </div>
  );
};

export default ImageUploader; 